import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle, DollarSign, Download, Plus, Send, RefreshCw, Search, X } from 'lucide-react';
import { erpAPI } from '../../services/api';
import SearchableSelect from '../../components/admin/SearchableSelect';
import Toast from '../../components/admin/Toast';
import FinancialDocumentDrawer from '../../components/admin/FinancialDocumentDrawer';
import { exportCSV } from '../../utils/exportUtils';
import { paymentAccountOptions } from '../../utils/erpHelpers';

const money = (v: any) => `\u09F3${Number(v || 0).toLocaleString('en-BD', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (v: any) => v ? new Date(v).toLocaleDateString('en-BD') : '-';
const dt = (v: any) => v ? new Date(v).toLocaleString('en-BD') : '-';
const rows = (p: any): any[] => Array.isArray(p) ? p : (p?.results || []);
const errorText = (e: any) => e?.response?.data?.detail || e?.message || 'Request failed';

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft', SUBMITTED: 'Submitted', APPROVED: 'Approved',
  UNPAID: 'Unpaid', PARTIAL: 'Partially Paid', PAID: 'Paid', CANCELLED: 'Cancelled',
};
const STATUS_ORDER: Record<string, number> = {
  DRAFT: 0, SUBMITTED: 1, APPROVED: 2, UNPAID: 2, PARTIAL: 3, PAID: 4, CANCELLED: 5,
};

export default function ExpensesPage({ me, locationFilter }: { me: any; locationFilter: string }) {
  const [data, setData] = useState<any[]>([]);
  const [summary, setSummary] = useState<Record<string, any>>({});
  const [locations, setLocations] = useState<any[]>([]);
  const [cashBankAccounts, setCashBankAccounts] = useState<any[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [payments, setPayments] = useState<any[]>([]);
  const [payLoading, setPayLoading] = useState(false);
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payForm, setPayForm] = useState<Record<string, any>>({});

  const can = useCallback((code: string) => Boolean(me?.is_superuser || me?.is_admin || (me?.permissions || []).includes(code)), [me]);
  const showLoc = useMemo(() => !locationFilter, [locationFilter]);
  const params = useMemo(() => ({ ...filters, ...(locationFilter ? { location: locationFilter } : {}) }), [filters, locationFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [resp, summ, locs, accts, pmts] = await Promise.all([
        erpAPI.list('expenses', { ...params, page_size: 200 }),
        erpAPI.list('expenses/summary', { ...(locationFilter ? { location: locationFilter } : {}) }),
        erpAPI.list('locations', {}),
        erpAPI.list('accounts-section/cash-bank', {}).catch(() => ({ data: { accounts: [] } })),
        erpAPI.list('payment-accounts', locationFilter ? { location: locationFilter } : {}).catch(() => ({ data: [] })),
      ]);
      setData(rows(resp.data)); setSummary(summ.data || {});
      setLocations(rows(locs.data)); setCashBankAccounts(accts.data?.accounts || []);
      setPaymentMethods(rows(pmts.data));
    } catch (e) { setMessage({ type: 'error', text: errorText(e) }); }
    finally { setLoading(false); }
  }, [params, locationFilter]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title || !form.amount || !form.location) { setMessage({ type: 'error', text: 'Title, amount, location required.' }); return; }
    setSaving(true);
    try {
      await erpAPI.create('expenses', { location: Number(form.location), title: form.title, amount: Number(form.amount), paid_amount: Number(form.paid_amount || 0), category: form.category || '', vendor: form.vendor || '', expense_date: form.expense_date || undefined, reference: form.reference || '', notes: form.notes || '' });
      setMessage({ type: 'success', text: 'Expense created.' }); setShowForm(false); setForm({}); load();
    } catch (e) { setMessage({ type: 'error', text: errorText(e) }); }
    finally { setSaving(false); }
  };

  const handleSubmit = async (id: number) => {
    try { await erpAPI.action('expenses', id, 'submit', {}); setMessage({ type: 'success', text: 'Expense submitted.' }); load(); }
    catch (e) { setMessage({ type: 'error', text: errorText(e) }); }
  };
  const handleApprove = async (id: number) => {
    try { await erpAPI.action('expenses', id, 'approve', {}); setMessage({ type: 'success', text: 'Expense approved.' }); load(); }
    catch (e) { setMessage({ type: 'error', text: errorText(e) }); }
  };
  const handleCancel = async (id: number) => {
    if (!window.confirm('Cancel this expense?')) return;
    try { await erpAPI.action('expenses', id, 'cancel', {}); setMessage({ type: 'success', text: 'Expense cancelled.' }); load(); }
    catch (e) { setMessage({ type: 'error', text: errorText(e) }); }
  };
  const handlePay = async () => {
    try {
      await erpAPI.action('expenses', payForm.expenseId, 'pay', { amount: Number(payForm.amount), payment_method: Number(payForm.method), cash_bank_account: Number(payForm.account), reference: payForm.reference || '' });
      setMessage({ type: 'success', text: 'Payment recorded.' }); setPayModalOpen(false); setPayForm({}); setDrawerOpen(false); load();
    } catch (e) { setMessage({ type: 'error', text: errorText(e) }); }
  };

  const openDrawer = async (exp: any) => {
    setSelected(exp); setPayLoading(true);
    try {
      const [txResp, pmtResp] = await Promise.all([
        erpAPI.list('accounts-section/transactions', { reference_type: 'Expense', reference_id: String(exp.id) }),
        erpAPI.list('accounts-section/transactions', { reference_type: 'ExpensePayment', reference_id: String(exp.id) }),
      ]);
      setPayments([...(txResp.data?.results || []), ...(pmtResp.data?.results || [])]);
    } catch { setPayments([]); }
    finally { setPayLoading(false); setDrawerOpen(true); }
  };

  const filtered = useMemo(() => {
    let list = [...data];
    if (filters.search) { const q = filters.search.toLowerCase(); list = list.filter(r => (r.expense_number || '').toLowerCase().includes(q) || (r.title || '').toLowerCase().includes(q) || (r.vendor || '').toLowerCase().includes(q)); }
    if (filters.status) list = list.filter(r => r.status === filters.status);
    if (filters.category) list = list.filter(r => r.category === filters.category);
    return list;
  }, [data, filters]);

  const uniqueCats = useMemo(() => [...new Set(data.map(r => r.category).filter(Boolean))] as string[], [data]);
  const locOpts = locations.filter((l: any) => l.is_active).map((l: any) => ({ value: l.id, label: l.name }));
const methodOpts = paymentAccountOptions(paymentMethods);
  const accountOpts = cashBankAccounts.map((x: any) => ({ value: x.id, label: `${x.account || x.name} - ${x.location_name}` }));
  const pickMethod = (mId: any) => {
    const m = paymentMethods.find((x: any) => String(x.id) === String(mId));
    const linked = m?.cash_bank_account ? Number(m.cash_bank_account) : (payForm.account || '');
    setPayForm(f => ({ ...f, method: mId, account: linked }));
  };

  const statusStyle = (s: string) => {
    const map: Record<string, { bg: string; color: string }> = {
      DRAFT: { bg: '#f1f5f9', color: '#475569' }, SUBMITTED: { bg: '#dbeafe', color: '#1e40af' },
      APPROVED: { bg: '#e0e7ff', color: '#4338ca' }, UNPAID: { bg: '#fef3c7', color: '#92400e' },
      PARTIAL: { bg: '#fed7aa', color: '#9a3412' }, PAID: { bg: '#d1fae5', color: '#065f46' },
      CANCELLED: { bg: '#fee2e2', color: '#991b1b' },
    }; return map[s] || { bg: '#f1f5f9', color: '#475569' };
  };

  return <div className="erp-page">
    <Toast show={Boolean(message)} message={message?.text || ''} type={message?.type || 'info'} onClose={() => setMessage(null)} />

    <div className="acct-kpi-grid">
      <div className="erp-stat"><span>Today's Expenses</span><strong style={{ color: '#dc2626' }}>{money(summary.today)}</strong></div>
      <div className="erp-stat"><span>Monthly</span><strong style={{ color: '#d97706' }}>{money(summary.month)}</strong></div>
      <div className="erp-stat"><span>Pending</span><strong style={{ color: '#2563eb' }}>{money(summary.pending)}</strong></div>
      <div className="erp-stat"><span>Outstanding</span><strong style={{ color: '#7c3aed' }}>{money(summary.outstanding)}</strong></div>
    </div>

    <div className="erp-heading-actions" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
      <div className="erp-filterbar" style={{ flex: 1, flexWrap: 'wrap' }}>
        <Search size={15} style={{ color: '#94a3b8' }} />
        <input value={filters.search || ''} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
          placeholder="Search expense #, title, vendor..." style={{ border: 0, background: 'transparent', flex: 1, minWidth: 180, outline: 'none' }} />
        <label>Status</label>
        <SearchableSelect value={filters.status || ''} onChange={n => setFilters(f => ({ ...f, status: n }))}
          placeholder="All" options={Object.entries(STATUS_LABELS).map(([k, v]) => ({ value: k, label: v }))} />
        {uniqueCats.length > 0 && <><label>Category</label>
          <SearchableSelect value={filters.category || ''} onChange={n => setFilters(f => ({ ...f, category: n }))}
            placeholder="All" options={uniqueCats.map(c => ({ value: c, label: c }))} /></>}
        {Object.keys(filters).some(k => filters[k]) && (
          <button onClick={() => setFilters({})} style={{ border: 0, background: '#fee2e2', color: '#991b1b', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontWeight: 600, fontSize: '.7rem' }}><X size={12} /> Clear</button>
        )}
        <button onClick={() => exportCSV(filtered, 'expenses')} className="erp-btn secondary" style={{ padding: '5px 8px', fontSize: '.7rem' }}><Download size={12} /> CSV</button>
      </div>
      {can('accounts.expense.create') && (
        <button className="erp-btn" onClick={() => { setForm({ expense_date: new Date().toISOString().slice(0, 10), paid_amount: 0 }); setShowForm(true); }}>
          <Plus size={15} /> Add Expense
        </button>
      )}
    </div>

    {showForm && <div className="acct-modal-overlay" onClick={() => setShowForm(false)}>
      <div className="acct-modal" style={{ maxWidth: 580 }} onClick={e => e.stopPropagation()}>
        <div className="acct-modal-header"><h3 className="acct-modal-title">New Expense</h3>
          <button className="acct-modal-close" onClick={() => setShowForm(false)}>✕</button></div>
        <div className="acct-modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="acct-form-group" style={{ gridColumn: '1/-1' }}><label className="acct-form-label">Title *</label>
              <input className="acct-form-input" value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Office Rent" /></div>
            <div className="acct-form-group"><label className="acct-form-label">Amount *</label>
              <input className="acct-form-input" type="number" value={form.amount || ''} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" /></div>
            <div className="acct-form-group"><label className="acct-form-label">Location *</label>
              <SearchableSelect value={form.location || ''} onChange={n => setForm(f => ({ ...f, location: n }))} placeholder="Select" options={locOpts} /></div>
            <div className="acct-form-group"><label className="acct-form-label">Category</label>
              <input className="acct-form-input" value={form.category || ''} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. Rent" list="exp-cats" />
              <datalist id="exp-cats">{['Rent','Utilities','Salaries','Marketing','Office Supplies','Transportation','Maintenance','Other'].map(c => <option key={c} value={c} />)}</datalist></div>
            <div className="acct-form-group"><label className="acct-form-label">Vendor</label>
              <input className="acct-form-input" value={form.vendor || ''} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))} placeholder="Optional" /></div>
            <div className="acct-form-group"><label className="acct-form-label">Date</label>
              <input className="acct-form-input" type="date" value={form.expense_date || ''} onChange={e => setForm(f => ({ ...f, expense_date: e.target.value }))} /></div>
            <div className="acct-form-group"><label className="acct-form-label">Paid Amount</label>
              <input className="acct-form-input" type="number" value={form.paid_amount ?? ''} onChange={e => setForm(f => ({ ...f, paid_amount: e.target.value }))} placeholder="0" /></div>
            <div className="acct-form-group" style={{ gridColumn: '1/-1' }}><label className="acct-form-label">Reference</label>
              <input className="acct-form-input" value={form.reference || ''} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} placeholder="Optional" /></div>
          </div>
          <div className="acct-form-actions" style={{ marginTop: 14 }}>
            <button className="acct-btn acct-btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="acct-btn acct-btn-primary" onClick={handleCreate} disabled={saving}>{saving ? 'Creating...' : 'Create Expense'}</button>
          </div>
        </div>
      </div>
    </div>}

    {loading ? <div className="erp-loading"><RefreshCw className="spin" /> Loading...</div> :
      <section className="erp-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="erp-table-wrap" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
          <table style={{ position: 'relative' }}>
            <thead><tr style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff' }}>
              <th>Expense #</th>{showLoc && <th>Location</th>}<th>Date</th><th>Title</th><th>Cat.</th><th>Amount</th><th>Paid</th><th>Due</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>{filtered.length === 0 ? <tr><td className="erp-empty" colSpan={showLoc ? 11 : 10}>No expenses found.</td></tr> :
              filtered.map((r: any) => <tr key={r.id}
                onClick={() => openDrawer(r)} style={{ cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ fontWeight: 600, fontSize: '.78rem', fontFamily: 'monospace' }}>{r.expense_number}</td>
                {showLoc && <td><span style={{ color: '#4f46e5', fontWeight: 600, fontSize: '.75rem' }}>{r.location_name || '-'}</span></td>}
                <td style={{ fontSize: '.78rem' }}>{date(r.expense_date)}</td>
                <td style={{ fontWeight: 500 }}>{r.title}</td>
                <td>{r.category ? <span style={{ padding: '2px 6px', borderRadius: 4, background: '#eff6ff', color: '#1d4ed8', fontSize: '.7rem', fontWeight: 600 }}>{r.category}</span> : '-'}</td>
                <td style={{ fontWeight: 600 }}>{money(r.amount)}</td>
                <td style={{ color: '#059669' }}>{money(r.paid_amount)}</td>
                <td style={{ fontWeight: 700, color: Number(r.due_amount || r.amount - r.paid_amount) > 0 ? '#dc2626' : '#059669' }}>
                  {money(r.due_amount || (r.amount - r.paid_amount))}</td>
                <td><span style={{ padding: '3px 7px', borderRadius: 999, fontSize: '.65rem', fontWeight: 700, ...statusStyle(r.status) }}>{STATUS_LABELS[r.status] || r.status}</span></td>
                <td><div className="erp-actions">
                  {r.status === 'DRAFT' && can('accounts.expense.create') &&
                    <button onClick={e => { e.stopPropagation(); handleSubmit(r.id); }}
                      style={{ border: 0, background: '#dbeafe', color: '#1e40af', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontWeight: 600, fontSize: '.65rem' }}>Submit</button>}
                  {r.status === 'SUBMITTED' && can('accounts.expense.create') &&
                    <button onClick={e => { e.stopPropagation(); handleApprove(r.id); }}
                      style={{ border: 0, background: '#d1fae5', color: '#065f46', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontWeight: 600, fontSize: '.65rem' }}>Approve</button>}
                  {r.status !== 'PAID' && r.status !== 'CANCELLED' && can('accounts.expense.update') &&
                    <button onClick={e => { e.stopPropagation(); handleCancel(r.id); }}
                      style={{ border: 0, background: '#fee2e2', color: '#991b1b', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontWeight: 600, fontSize: '.65rem' }}>Cancel</button>}
                </div></td>
              </tr>)}</tbody>
          </table>
        </div>
      </section>}

    {payModalOpen && selected && <div className="acct-modal-overlay" onClick={() => setPayModalOpen(false)}>
      <div className="acct-modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="acct-modal-header"><h3 className="acct-modal-title">Pay Expense</h3>
          <button className="acct-modal-close" onClick={() => setPayModalOpen(false)}>✕</button></div>
        <div className="acct-modal-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="acct-form-group"><label className="acct-form-label">Remaining Due</label>
              <input className="acct-form-input" value={money(payForm.remaining)} readOnly style={{ fontWeight: 700, color: '#dc2626' }} /></div>
            <div className="acct-form-row">
              <div className="acct-form-group"><label className="acct-form-label">Amount *</label>
                <input className="acct-form-input" type="number" value={payForm.amount || ''} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))} max={payForm.remaining} /></div>
              <div className="acct-form-group"><label className="acct-form-label">Payment Account *</label>
                <SearchableSelect value={payForm.method || ''} onChange={pickMethod} placeholder="Select" options={methodOpts} /></div>
            </div>
            <div className="acct-form-group"><label className="acct-form-label">From Account *</label>
              <SearchableSelect value={payForm.account || ''} onChange={n => setPayForm(f => ({ ...f, account: n }))} placeholder="Select" options={accountOpts} /></div>
            <div className="acct-form-group"><label className="acct-form-label">Reference</label>
              <input className="acct-form-input" value={payForm.reference || ''} onChange={e => setPayForm(f => ({ ...f, reference: e.target.value }))} placeholder="Optional" /></div>
            <div className="acct-form-actions">
              <button className="acct-btn acct-btn-secondary" onClick={() => setPayModalOpen(false)}>Cancel</button>
              <button className="acct-btn acct-btn-primary" onClick={handlePay}><DollarSign size={15} /> Record Payment</button>
            </div>
          </div>
        </div>
      </div>
    </div>}

    <FinancialDocumentDrawer
      open={drawerOpen} onClose={() => setDrawerOpen(false)}
      documentType="expense" title={`Expense ${selected?.expense_number || ''}`} subtitle={selected?.title}
      fields={[
        { label: 'Amount', value: <span style={{ fontWeight: 700, fontSize: '1rem' }}>{money(selected?.amount)}</span> },
        { label: 'Paid', value: <span style={{ color: '#059669', fontWeight: 600 }}>{money(selected?.paid_amount)}</span> },
        { label: 'Due', value: <span style={{ color: '#dc2626', fontWeight: 600 }}>{money(selected?.due_amount || (selected?.amount - selected?.paid_amount))}</span> },
        { label: 'Business Location', value: <span style={{ color: '#4f46e5', fontWeight: 600 }}>{selected?.location_name || '-'}</span> },
        { label: 'Category', value: selected?.category || '-' }, { label: 'Vendor', value: selected?.vendor || '-' },
        { label: 'Reference', value: selected?.reference || '-' }, { label: 'Date', value: date(selected?.expense_date) },
        { label: 'Created By', value: selected?.created_by_name || '-' },
      ]}
      timeline={(() => {
        const s = selected?.status || 'DRAFT'; const order = STATUS_ORDER[s] ?? 0;
        const steps: any[] = [
          { label: 'Draft', date: selected?.created_at ? dt(selected.created_at) : undefined, active: order >= 0 },
          { label: 'Submitted', active: order >= 1 }, { label: 'Approved', active: order >= 2 },
          { label: 'Journal Posted', active: order >= 2 },
          { label: s === 'PARTIAL' ? 'Partially Paid' : 'Paid', active: order >= 3 },
          s === 'CANCELLED' ? { label: 'Cancelled', active: true } : { label: 'Closed', active: order >= 4 },
        ]; return steps.filter(Boolean);
      })()}
      payments={payments.map((p: any) => ({ date: p.date, method: p.payment_method, account: p.account_name, reference: p.reference, user: p.created_by, amount: p.debit || p.credit || 0 }))}
      paymentsLoading={payLoading}
      actions={[
        selected?.status === 'DRAFT' ? { label: 'Submit', icon: <Send size={13} />, onClick: () => handleSubmit(selected.id) } : null,
        selected?.status === 'SUBMITTED' ? { label: 'Approve', icon: <CheckCircle size={13} />, onClick: () => handleApprove(selected.id), primary: true } : null,
        Number(selected?.due_amount || (selected?.amount - selected?.paid_amount)) > 0 && selected?.status !== 'DRAFT' && selected?.status !== 'CANCELLED'
          ? { label: 'Record Payment', icon: <DollarSign size={13} />, onClick: () => { setPayForm({ expenseId: selected.id, remaining: selected.due_amount || (selected.amount - selected.paid_amount), amount: selected.due_amount || (selected.amount - selected.paid_amount), method: '', account: '', reference: '' }); setPayModalOpen(true); }, primary: true } : null,
      ].filter(Boolean) as any}
      notes={selected?.notes}
    />
  </div>;
}
